import { createContext, useMemo, useCallback } from "react";
import { useAuth } from "../hooks";

export const PermissionsContext = createContext();

/**
 * Super Admin role ID - this role has all permissions by default
 */
const SUPER_ADMIN_ROLE_ID = 1;

/**
 * PermissionsProvider component that manages user permissions state
 * and provides permission checking utilities to child components.
 *
 * Permissions are loaded directly from the user object returned by /me endpoint.
 * Expected user.permissions format: ["module.action", "module.action", ...]
 * Example: ["grade.create", "grade.read", "student.read"]
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function PermissionsProvider({ children }) {
  const { user } = useAuth();

  /**
   * Check if the current user is a super admin
   */
  const isSuperAdmin = useMemo(() => {
    return user?.roleId === SUPER_ADMIN_ROLE_ID;
  }, [user?.roleId]);

  /**
   * Get permissions from user object
   * Permissions come directly from /me endpoint in format: ["module.action", ...]
   */
  const permissions = useMemo(() => {
    if (!user) return [];

    // Super admin has wildcard permission
    if (isSuperAdmin) return ["*"];

    // Return permissions array from user, or empty array if not present
    return Array.isArray(user.permissions) ? user.permissions : [];
  }, [user, isSuperAdmin]);

  /**
   * Loading state - false since permissions come with user data
   */
  const loading = useMemo(() => !user, [user]);

  /**
   * Check if user has a specific permission
   * Format: "module.action" (e.g., "user.create", "grade.read")
   *
   * @param {string} module - The module name (e.g., "user", "grade")
   * @param {string} action - The action name (e.g., "create", "read", "update", "delete")
   * @returns {boolean} - Whether the user has the permission
   */
  const hasPermission = useCallback(
    (module, action) => {
      // Super admin has all permissions
      if (isSuperAdmin || permissions.includes("*")) {
        return true;
      }

      const permissionKey = `${module}.${action}`;
      return permissions.includes(permissionKey);
    },
    [permissions, isSuperAdmin]
  );

  /**
   * Check if user can create in a module
   * @param {string} module - The module name
   */
  const canCreate = useCallback(
    (module) => hasPermission(module, "create"),
    [hasPermission]
  );

  /**
   * Check if user can read/view in a module
   * @param {string} module - The module name
   */
  const canRead = useCallback(
    (module) => hasPermission(module, "read"),
    [hasPermission]
  );

  /**
   * Check if user can update in a module
   * @param {string} module - The module name
   */
  const canUpdate = useCallback(
    (module) => hasPermission(module, "update"),
    [hasPermission]
  );

  /**
   * Check if user can delete in a module
   * @param {string} module - The module name
   */
  const canDelete = useCallback(
    (module) => hasPermission(module, "delete"),
    [hasPermission]
  );

  /**
   * Check if user has any permission for a module (for menu visibility)
   * @param {string} module - The module name
   */
  const canAccess = useCallback(
    (module) => {
      if (isSuperAdmin || permissions.includes("*")) {
        return true;
      }

      // Check if user has any permission for this module
      return permissions.some((perm) => perm.startsWith(`${module}.`));
    },
    [permissions, isSuperAdmin]
  );

  const value = useMemo(
    () => ({
      permissions,
      loading,
      isSuperAdmin,
      hasPermission,
      canCreate,
      canRead,
      canUpdate,
      canDelete,
      canAccess,
    }),
    [
      permissions,
      loading,
      isSuperAdmin,
      hasPermission,
      canCreate,
      canRead,
      canUpdate,
      canDelete,
      canAccess,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}
